"use client"

import { useQuery } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import {
  listDeliveryIntegrationsAction,
  listDeliveryIntegrationSenderProfilesAction,
  listDeliveryProvidersAction,
  searchDeliveryIntegrationLocationsAction,
  type DeliveryIntegrationView,
  type ListDeliveryIntegrationSenderProfilesInput,
  type SearchDeliveryIntegrationLocationsInput,
} from "@/server-actions/delivery-integrations"
import type {
  DeliveryLocationSearchResult,
  DeliverySenderProfileResult,
} from "@/lib/deliveries/types"

export const deliveryProvidersQueryKey = (tenantId: string) =>
  ["delivery-providers", tenantId] as const

export const deliveryIntegrationsQueryKey = (tenantId: string) =>
  ["delivery-integrations", tenantId] as const

export const deliveryIntegrationLocationsQueryKey = (
  tenantId: string,
  input: SearchDeliveryIntegrationLocationsInput,
) => [
  "delivery-integration-locations",
  tenantId,
  input.provider,
  getIntegrationCredentialCacheKey(input),
  input.countryCode,
  input.settlementQuery,
  input.query,
  input.types,
  input.limit,
  input.cursor ?? "first",
] as const

export const deliveryIntegrationSenderProfilesQueryKey = (
  tenantId: string,
  input: ListDeliveryIntegrationSenderProfilesInput,
) => [
  "delivery-integration-sender-profiles",
  tenantId,
  input.provider,
  getIntegrationCredentialCacheKey(input),
] as const

export function useDeliveryProvidersQuery() {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getDeliveryQueryTenantId(user)

  return useQuery({
    queryKey: deliveryProvidersQueryKey(tenantId),
    enabled: isAuthenticated,
    staleTime: 60 * 60_000,
    queryFn: async () => {
      const result = await listDeliveryProvidersAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useDeliveryIntegrationsQuery() {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getDeliveryQueryTenantId(user)

  return useQuery<DeliveryIntegrationView[]>({
    queryKey: deliveryIntegrationsQueryKey(tenantId),
    enabled: isAuthenticated,
    queryFn: async () => {
      const result = await listDeliveryIntegrationsAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useDeliveryIntegrationLocationsQuery(
  input: SearchDeliveryIntegrationLocationsInput,
  enabled: boolean,
) {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getDeliveryQueryTenantId(user)

  return useQuery<DeliveryLocationSearchResult>({
    queryKey: deliveryIntegrationLocationsQueryKey(tenantId, input),
    enabled: isAuthenticated && enabled,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () => {
      const result = await searchDeliveryIntegrationLocationsAction(input)
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useDeliveryIntegrationSenderProfilesQuery(
  input: ListDeliveryIntegrationSenderProfilesInput,
  enabled: boolean,
) {
  const { user, isAuthenticated } = useAuth()
  const tenantId = getDeliveryQueryTenantId(user)

  return useQuery<DeliverySenderProfileResult>({
    queryKey: deliveryIntegrationSenderProfilesQueryKey(tenantId, input),
    enabled: isAuthenticated && enabled,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () => {
      const result = await listDeliveryIntegrationSenderProfilesAction(input)
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function getDeliveryQueryTenantId(user: {
  tenant_id?: string
  impersonated_tenant_id?: string
} | null | undefined): string {
  return user?.impersonated_tenant_id
    ?? user?.tenant_id
    ?? "unauthenticated"
}

function getIntegrationCredentialCacheKey(
  input:
    | SearchDeliveryIntegrationLocationsInput
    | ListDeliveryIntegrationSenderProfilesInput,
): string {
  switch (input.provider) {
    case "nova_post":
      return getNovaPostCredentialCacheKey(input)
    default: {
      const exhaustiveProvider: never = input.provider
      return exhaustiveProvider
    }
  }
}

function getNovaPostCredentialCacheKey(
  input:
    | SearchDeliveryIntegrationLocationsInput
    | ListDeliveryIntegrationSenderProfilesInput,
): string {
  switch (input.credentialSource) {
    case "draft":
      return `draft:${input.searchSessionId}`
    case "integration":
      return `integration:${input.integrationId}`
    default: {
      const exhaustiveSource: never = input
      return exhaustiveSource
    }
  }
}
